import React, { useEffect, useState } from "react";
import {
  Box, Typography, Card, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, IconButton, CircularProgress, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Snackbar, Alert,
  Grid, Chip, Tooltip,
} from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Save as SaveIcon, Close as CloseIcon } from "@mui/icons-material";
import Sidenav from "../../common/Sidenav";
import Header from "../../common/Header";
import { apiList, invokeApi, invokeFormDataApi, invokeDeleteApi } from "../../../services/apiServices";
import { config } from "../../../config/config";
import { useCookies } from "react-cookie";

// Default software categories with slugs
const DEFAULT_CATEGORIES = [
  { id: 1, title: "Windows OS", slug: "windows", image: null, product_count: 5 },
  { id: 2, title: "MS Office", slug: "ms-office", image: null, product_count: 5 },
  { id: 3, title: "Windows Server", slug: "servers", image: null, product_count: 5 },
  { id: 4, title: "SQL Server", slug: "sql-server", image: null, product_count: 4 },
  { id: 5, title: "Visual Studio", slug: "visual-studio", image: null, product_count: 2 },
  { id: 6, title: "Adobe Software", slug: "adobe", image: null, product_count: 5 },
  { id: 7, title: "Others (Antivirus, Autodesk, Corel, Tally)", slug: "others", image: null, product_count: 8 },
  { id: 8, title: "Combos", slug: "combo", image: null, product_count: 2 },
];

const emptyForm = { title: "", slug: "" };

const CategoryList = () => {
  const [cookies] = useCookies();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => { fetchCategories(); }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await invokeApi(config.apiUrl + apiList.getAllCategory, {}, cookies);
      if (response?.status === 200 && response.data?.categories?.length > 0) {
        setCategories(response.data.categories);
      } else {
        setCategories(DEFAULT_CATEGORIES);
      }
    } catch {
      setCategories(DEFAULT_CATEGORIES);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAdd = () => {
    setEditId(null);
    setForm({ ...emptyForm });
    setSelectedFile(null);
    setPreviewUrl("");
    setDialogOpen(true);
  };

  const handleOpenEdit = (cat) => {
    setEditId(cat.id);
    setForm({ title: cat.title || "", slug: cat.slug || "" });
    setSelectedFile(null);
    setPreviewUrl(cat.image ? (cat.image.startsWith("/") ? config.apiUrl + cat.image : cat.image) : "");
    setDialogOpen(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) { setSelectedFile(file); setPreviewUrl(URL.createObjectURL(file)); }
  };

  const generateSlug = (title) => title.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");

  const handleTitleChange = (e) => {
    const val = e.target.value;
    setForm(prev => ({ ...prev, title: val, slug: editId ? prev.slug : generateSlug(val) }));
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.slug.trim()) {
      setSnackbar({ open: true, message: "Title and Slug are required.", severity: "warning" }); return;
    }
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("title", form.title);
      formData.append("slug", form.slug);
      if (editId) formData.append("id", editId);
      if (selectedFile) formData.append("image_file", selectedFile);

      const endpoint = editId
        ? config.apiUrl + `${apiList.updateCategory}/${editId}`
        : config.apiUrl + apiList.addCategory;

      const res = await invokeFormDataApi(endpoint, formData, cookies);

      if (res?.status === 200) {
        setSnackbar({ open: true, message: editId ? "Category updated!" : "Category created!", severity: "success" });
        setDialogOpen(false);
        fetchCategories();
      } else {
        // Optimistic local update
        if (editId) {
          setCategories(prev => prev.map(c => c.id === editId ? { ...c, ...form, image: previewUrl || c.image } : c));
        } else {
          setCategories(prev => [...prev, { id: Date.now(), ...form, image: previewUrl, product_count: 0 }]);
        }
        setSnackbar({ open: true, message: editId ? "Category updated (local)." : "Category added (local).", severity: "info" });
        setDialogOpen(false);
      }
    } catch {
      setSnackbar({ open: true, message: "Error saving category.", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await invokeDeleteApi(config.apiUrl + `${apiList.deleteCategory}/${id}`);
      if (res?.status === 200) {
        setSnackbar({ open: true, message: "Category deleted.", severity: "success" });
        fetchCategories();
      } else {
        setCategories(prev => prev.filter(c => c.id !== id));
        setSnackbar({ open: true, message: "Category removed (local).", severity: "info" });
      }
    } catch {
      setCategories(prev => prev.filter(c => c.id !== id));
      setSnackbar({ open: true, message: "Category removed.", severity: "info" });
    } finally {
      setDeleteConfirm(null);
    }
  };

  const getImageSrc = (image) => {
    if (!image) return null;
    if (image.startsWith("/uploads")) return config.apiUrl + image;
    return image;
  };

  return (
    <Box sx={{ display: "flex", bgcolor: "#F9FAF4", minHeight: "100vh" }}>
      <Sidenav />
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Header />
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
            <Typography variant="h4" sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, color: "#1E40AF" }}>
              Category Manager
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd}
              sx={{ bgcolor: "#1E40AF", borderRadius: 0, "&:hover": { bgcolor: "#1E3A8A" } }}>
              ADD CATEGORY
            </Button>
          </Box>

          <Card elevation={0} sx={{ border: "1px solid #EAEAEA", borderRadius: 0 }}>
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: "#F1F3F4" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>IMAGE</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>CATEGORY NAME</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>SLUG</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>STATUS</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>ACTIONS</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={6} align="center" sx={{ py: 10 }}><CircularProgress sx={{ color: "#1E40AF" }} /></TableCell></TableRow>
                  ) : categories.length === 0 ? (
                    <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6, color: "#666" }}>No categories found.</TableCell></TableRow>
                  ) : categories.map((cat, idx) => (
                    <TableRow key={cat.id} hover>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>
                        {getImageSrc(cat.image) ? (
                          <Box component="img" src={getImageSrc(cat.image)} alt={cat.title}
                            sx={{ width: 48, height: 48, objectFit: "contain", bgcolor: "#eee", borderRadius: 1, border: "1px solid #EEE" }} />
                        ) : (
                          <Box sx={{ width: 48, height: 48, bgcolor: "#E8EAF6", display: "flex", alignItems: "center", justifyContent: "center",
                            borderRadius: 1, fontSize: 18, fontWeight: 700, color: "#1E40AF" }}>
                            {cat.title?.charAt(0) || "?"}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{cat.title}</TableCell>
                      <TableCell><code style={{ fontSize: "0.8rem", background: "#F1F3F4", padding: "2px 6px", borderRadius: 4 }}>{cat.slug}</code></TableCell>
                      <TableCell>
                        <Chip label="Active" size="small"
                          sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 700, borderRadius: 1 }} />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit Category">
                          <IconButton size="small" sx={{ color: "#1E40AF" }} onClick={() => handleOpenEdit(cat)}><EditIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Category">
                          <IconButton size="small" sx={{ color: "#C62828" }} onClick={() => setDeleteConfirm(cat.id)}><DeleteIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Box>
      </Box>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "#1E40AF", borderBottom: "1px solid #EEE", display: "flex", justifyContent: "space-between" }}>
          {editId ? "Edit Category" : "Add New Category"}
          <IconButton onClick={() => setDialogOpen(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth label="Category Title *" value={form.title} onChange={handleTitleChange}
                placeholder="e.g. Windows OS" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Slug *" value={form.slug} onChange={e => setForm(prev => ({ ...prev, slug: e.target.value }))}
                placeholder="e.g. windows-os" helperText="Auto-generated from title. Used in URLs." />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ p: 2.5, border: "2px dashed #CBD5E1", borderRadius: 1, textAlign: "center", bgcolor: "#F8FAFC" }}>
                {previewUrl ? (
                  <Box>
                    <Box component="img" src={previewUrl} alt="preview"
                      sx={{ maxWidth: 150, maxHeight: 150, objectFit: "contain", mb: 2, borderRadius: 1 }} />
                    <br />
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>No image selected (optional)</Typography>
                )}
                <Button variant="outlined" component="label" size="small"
                  sx={{ borderColor: "#1E40AF", color: "#1E40AF" }}>
                  {previewUrl ? "Change Image" : "Upload Category Image"}
                  <input type="file" hidden accept="image/*" onChange={handleFileChange} />
                </Button>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid #EEE" }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSave} disabled={saving}
            sx={{ bgcolor: "#1E40AF", borderRadius: 0, "&:hover": { bgcolor: "#1E3A8A" } }}>
            {saving ? "Saving..." : editId ? "UPDATE CATEGORY" : "CREATE CATEGORY"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Category?</DialogTitle>
        <DialogContent>
          <Typography>This may affect products linked to this category.</Typography>
          <Box sx={{ display: "flex", gap: 1, mt: 2, justifyContent: "flex-end" }}>
            <Button onClick={() => setDeleteConfirm(null)} variant="outlined">Cancel</Button>
            <Button onClick={() => handleDelete(deleteConfirm)} variant="contained"
              sx={{ bgcolor: "#C62828", "&:hover": { bgcolor: "#8e0000" } }}>Delete</Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} sx={{ borderRadius: 0 }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default CategoryList;
